import React, { useCallback, useEffect, useState } from "react";
import { Table, Button, Modal, Input, Segmented, Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import emailCampaignApi from "../../../api/emailCampaignApi";
import EmailCampaignSummaryBar from "./email-campaign-summary-bar";
import { buildEmailCampaignColumns, STATUS_TAG } from "./email-campaign-table-columns";

// Khi còn chiến dịch đang gửi thì tự tải lại để thanh tiến độ chạy theo queue worker.
const AUTO_REFRESH_MS = 10000;

function EmailCampaignList() {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState([]);
    const [campaignStats, setCampaignStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [statusFilter, setStatusFilter] = useState("all");
    const [keyword, setKeyword] = useState("");
    const [sendingId, setSendingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // silent: tải ngầm (auto refresh) — không bật spinner che bảng
    const fetchCampaigns = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const { data } = await emailCampaignApi.listCampaigns({
                limit: pagination.pageSize,
                offset: (pagination.current - 1) * pagination.pageSize,
                status: statusFilter === "all" ? undefined : statusFilter,
                q: keyword || undefined,
            });
            setCampaigns(data.data.items || []);
            setPagination((prev) => ({ ...prev, total: data.data.total || 0 }));
            if (data.data.stats) setCampaignStats(data.data.stats);
        } catch {
            if (!silent) message.error("Không thể tải danh sách chiến dịch");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, statusFilter, keyword]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    const hasSending = (campaignStats.sending || 0) > 0;
    useEffect(() => {
        if (!hasSending) return undefined;
        const timer = setInterval(() => fetchCampaigns(true), AUTO_REFRESH_MS);
        return () => clearInterval(timer);
    }, [hasSending, fetchCampaigns]);

    // Đổi bộ lọc thì về trang 1
    const changeStatus = (value) => {
        setStatusFilter(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };
    const changeKeyword = (value) => {
        setKeyword(value.trim());
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleSend = (record) => {
        Modal.confirm({
            title: "Xác nhận gửi chiến dịch",
            content: `Gửi email "${record.name}" đến tất cả users phù hợp? (hệ thống sẽ tự đếm khi gửi)`,
            okText: "Gửi ngay",
            okType: "danger",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    setSendingId(record.campaignId);
                    await emailCampaignApi.sendCampaign(record.campaignId);
                    message.success("Đã bắt đầu gửi chiến dịch");
                    fetchCampaigns();
                } catch (err) {
                    message.error(err?.response?.data?.message || "Gửi thất bại");
                } finally {
                    setSendingId(null);
                }
            },
        });
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Xác nhận xóa",
            content: `Xóa chiến dịch "${record.name}"? Hành động này không thể hoàn tác.`,
            okText: "Xóa",
            okType: "danger",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    setDeletingId(record.campaignId);
                    await emailCampaignApi.deleteCampaign(record.campaignId);
                    message.success("Đã xóa chiến dịch");
                    fetchCampaigns();
                } catch (err) {
                    message.error(err?.response?.data?.message || "Xóa thất bại");
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    const columns = buildEmailCampaignColumns({ navigate, onSend: handleSend, onDelete: handleDelete, sendingId, deletingId });

    const statusOptions = [
        { value: "all", label: `Tất cả (${(campaignStats.total || 0).toLocaleString()})` },
        ...Object.entries(STATUS_TAG).map(([value, t]) => ({
            value,
            label: `${t.label} (${(campaignStats[value] || 0).toLocaleString()})`,
        })),
    ];

    return (
        <div className="flex-1 p-6 overflow-auto bg-surface-sunken">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                <div>
                    <h1 className="text-xl font-semibold m-0 text-content">Email Campaign</h1>
                    <div className="text-[13px] text-content-3 mt-0.5">Gửi email hàng loạt tới người dùng qua Amazon SES</div>
                </div>
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => navigate("/email/create")}
                >
                    Tạo chiến dịch mới
                </Button>
            </div>

            <EmailCampaignSummaryBar stats={campaignStats} />

            <div className="rounded-xl border border-line bg-surface p-4">
                <div className="flex flex-wrap gap-3 justify-between items-center mb-3">
                    <div className="max-w-full overflow-x-auto">
                        <Segmented options={statusOptions} value={statusFilter} onChange={changeStatus} />
                    </div>
                    <div className="flex gap-2 items-center">
                        <Input.Search
                            allowClear
                            placeholder="Tìm tên chiến dịch / tiêu đề email"
                            onSearch={changeKeyword}
                            style={{ width: 280 }}
                        />
                        <Tooltip title="Tải lại">
                            <Button icon={<FontAwesomeIcon icon={faRotateRight} />} onClick={() => fetchCampaigns()} />
                        </Tooltip>
                    </div>
                </div>
                <Table
                    rowKey="campaignId"
                    dataSource={campaigns}
                    columns={columns}
                    loading={loading}
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: keyword || statusFilter !== "all" ? "Không có chiến dịch khớp bộ lọc" : "Chưa có chiến dịch nào" }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (t) => `${t.toLocaleString()} chiến dịch`,
                        onChange: (page, pageSize) =>
                            setPagination((prev) => ({ ...prev, current: page, pageSize })),
                    }}
                />
            </div>
        </div>
    );
}

export default EmailCampaignList;
